// autopilot-doctor-manifest-checks.ts
//
// Split out of autopilot-doctor.ts (was 223 lines, over the 200-line budget
// — review 260909-p6-m2 INFO). Owns the three `compatibility.json`-derived
// checks: settings/profile digest tamper detection, pack pin drift, and
// journal (MissionStore) status. `runAutopilotDoctorChecks` in
// autopilot-doctor.ts composes these with the P2 preflight checks it reuses
// directly.
import { join } from "node:path";
import { pathExists, readJson } from "../filesystem-helpers.js";
import { readPinnedPackVersion } from "../team-pack-manager.js";
import { computeSettingsProfileDigest, type ToolStateFile } from "./mission-settings-profile.js";
import { MissionStore } from "./mission-store.js";
import type { Compatibility } from "./schemas/compatibility-schema.js";

export interface AutopilotCheckResult {
  name: string;
  status: "ok" | "warn" | "fail";
  detail: string;
}

export async function checkCompatibilityDigests(
  controlRoot: string,
  missionWorkspace: string,
): Promise<AutopilotCheckResult> {
  const compatPath = join(controlRoot, "compatibility.json");
  if (!(await pathExists(compatPath))) {
    return {
      name: "Compatibility manifest",
      status: "warn",
      detail: "compatibility.json chưa tồn tại — mission chưa qua init hoàn tất.",
    };
  }
  const compat = await readJson<Compatibility>(compatPath);
  const settingsPath = join(missionWorkspace, ".claude", "settings.json");
  const toolStatePath = join(missionWorkspace, ".claude", "avatar-tools.json");
  if (!(await pathExists(settingsPath))) {
    return {
      name: "Settings/profile digest",
      status: "fail",
      detail: `settings.json không tồn tại tại ${settingsPath}.`,
    };
  }
  // Same formula as mission-workspace.ts's computeSettingsProfileDigest (D06/N36)
  // — comparing raw-file sha256 against a combined settings+profile digest would
  // NEVER match even when nothing was tampered with. Missing/corrupt
  // avatar-tools.json is itself evidence of tampering (fails closed as "warn").
  let liveSha: string;
  try {
    const settings = await readJson<Record<string, unknown>>(settingsPath);
    const toolState = await readJson<ToolStateFile>(toolStatePath);
    liveSha = computeSettingsProfileDigest(settings, toolState);
  } catch {
    return {
      name: "Settings/profile digest",
      status: "warn",
      detail: `settings.json hoặc avatar-tools.json không đọc/parse được tại ${missionWorkspace}/.claude — không tính được digest để so compatibility.json.`,
    };
  }

  if (liveSha !== compat.settings_digest) {
    return {
      name: "Settings/profile digest",
      status: "warn",
      detail:
        `settings mission BỊ SỬA ngoài supervisor — sha256 hiện tại (${liveSha}) ` +
        `khác compatibility.json (${compat.settings_digest}).`,
    };
  }
  return {
    name: "Settings/profile digest",
    status: "ok",
    detail: `khớp compatibility.json (settings_digest=${compat.settings_digest}, profile_digest=${compat.profile_digest}).`,
  };
}

export async function checkPackPin(
  controlRoot: string,
  missionWorkspace: string,
): Promise<AutopilotCheckResult> {
  const compatPath = join(controlRoot, "compatibility.json");
  if (!(await pathExists(compatPath))) {
    return { name: "Pack pin", status: "warn", detail: "compatibility.json chưa tồn tại." };
  }
  const compat = await readJson<Compatibility>(compatPath);
  const currentPin = await readPinnedPackVersion(missionWorkspace).catch(() => "(unknown)");
  return {
    name: "Pack pin",
    status: currentPin === compat.pack.version ? "ok" : "warn",
    detail: `manifest=${compat.pack.version} hiện tại=${currentPin}`,
  };
}

export async function checkJournalStatus(
  controlRoot: string,
): Promise<{ check: AutopilotCheckResult; blocksRun: boolean }> {
  const store = await MissionStore.open(controlRoot);
  try {
    if (store.status === "RECOVERING") {
      return {
        check: {
          name: "Journal status",
          status: "fail",
          detail: `RECOVERING — ${store.recoveringReason ?? "cần điều tra thủ công"}. 'run' bị chặn.`,
        },
        blocksRun: true,
      };
    }
    return {
      check: { name: "Journal status", status: "ok", detail: "READY" },
      blocksRun: false,
    };
  } finally {
    await store.close();
  }
}
